
import { computed, defineComponent, reactive } from 'vue';
import { IOption } from '@/core/models/AntDesign/IOption';
import { IParametrosConsultaRapida } from '@/core/models/Consulta/IParametrosConsultaRapida';
import { useTelaBase } from '@/core/composables/TelaBase';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';
import ServicoCaracteristica from '@/servicos/Cadastros/Produtos/ServicoCaracteristica';
import storeSistema from '@/store/storeSistema';

export default defineComponent({
  name: 'SelecionarCaracteristicaSubItem',
  props: {
    varios: {
      type: Boolean,
      default: false,
    },
    codigoSelecionado: {
      type: Number || undefined,
    },
    codigosSelecionados: {
      type: Array as () => number[],
      default: () => [],
    },
    codigoCaracteristicaItem: {
      type: Number,
      default: 0,
    },
    empresas: {
      type: Array as () => number[],
      default: () => [],
    },
    filtrarApenasItemPreenchido: {
      type: Boolean,
      default: true,
    },
    limparSelecao: {
      type: Boolean,
      default: true,
    },
    placeholder: {
      type: String,
      default: 'Digite para selecionar',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:codigoSelecionado', 'update:codigosSelecionados', 'change'],
  setup(props, { emit }) {
    const { montaOpcoesComListaConsultaRapida } = useTelaBase();

    let debounce = 0;
    const servicoCaracteristica = new ServicoCaracteristica();

    const state = reactive({
      listaCaracteristicasSubItens: [] as IOption[],
      buscandoDados: false,
      emDigitacao: false,
    });

    async function obterCaracteristicaSubItemPorCodigo(valor: any) {
      if (state.emDigitacao) { return; }
      if (!UtilitarioGeral.valorValido(valor)) { return; }
      if (!(valor > 0)) { return; }

      if (props.varios) {
        if (props.codigosSelecionados.length === 0) { return; }
      } else if (props.codigoSelecionado === 0) {
        return;
      }
      let efetuarConsulta = true;

      if (UtilitarioGeral.validaLista(state.listaCaracteristicasSubItens)) {
        if (props.varios) {
          const listaCodigosConsulta = state.listaCaracteristicasSubItens.map((c) => c.value);
          const diferencas = props.codigosSelecionados.filter((c) => !listaCodigosConsulta.includes(c));
          if (!UtilitarioGeral.validaLista(diferencas)) {
            efetuarConsulta = false;
          }
        } else {
          const existeLista = state.listaCaracteristicasSubItens.find((c) => c.value === props.codigoSelecionado);
          if (existeLista !== undefined) {
            efetuarConsulta = false;
          }
        }
      }

      if (!efetuarConsulta) return;

      state.listaCaracteristicasSubItens = [];

      const parametrosConsultaRapida: IParametrosConsultaRapida = {
        apenasAtivos: false, recursoAssociado: '',
      };

      if (UtilitarioGeral.validaLista(props.empresas)) {
        parametrosConsultaRapida.empresas = props.empresas;
      } else {
        parametrosConsultaRapida.empresas = storeSistema.getters.empresasUsuarioAutenticado();
      }

      if (props.varios) {
        parametrosConsultaRapida.filtrarPorVariosCodigos = true;
        parametrosConsultaRapida.valores = valor;
      } else {
        parametrosConsultaRapida.filtrarPorCodigo = true;
        parametrosConsultaRapida.valor = valor;
      }

      const listaCaracteristicasSubItens = await servicoCaracteristica.consultaRapidaSubItens(props.codigoCaracteristicaItem, parametrosConsultaRapida);
      state.listaCaracteristicasSubItens = montaOpcoesComListaConsultaRapida(listaCaracteristicasSubItens);
      if (!UtilitarioGeral.validaLista(listaCaracteristicasSubItens)) {
        if (props.varios) {
          emit('update:codigosSelecionados', undefined);
        } else {
          emit('update:codigoSelecionado', undefined);
        }
      }
    }

    async function pesquisarCaracteristicaSubItem(valorPesquisa: any) {
      state.emDigitacao = true;
      state.listaCaracteristicasSubItens = [];

      if (props.filtrarApenasItemPreenchido) {
        if (!(props.codigoCaracteristicaItem > 0)) { return; }
      }

      state.buscandoDados = true;
      clearTimeout(debounce);
      debounce = setTimeout(async () => {
        const parametrosConsultaRapida: IParametrosConsultaRapida = {
          valor: valorPesquisa, apenasAtivos: true, recursoAssociado: '', quantidadeRegistros: 100,
        };

        if (UtilitarioGeral.validaLista(props.empresas)) {
          parametrosConsultaRapida.empresas = props.empresas;
        } else {
          parametrosConsultaRapida.empresas = storeSistema.getters.empresasUsuarioAutenticado();
        }

        const listaCaracteristicasSubItens = await servicoCaracteristica.consultaRapidaSubItens(props.codigoCaracteristicaItem, parametrosConsultaRapida);
        state.buscandoDados = false;
        state.listaCaracteristicasSubItens = montaOpcoesComListaConsultaRapida(listaCaracteristicasSubItens);
      }, 600);
    }

    function obterCaracteristicaSubItem():any {
      if (props.codigoSelecionado !== undefined) {
        if (props.codigoSelecionado > 0) {
          obterCaracteristicaSubItemPorCodigo(props.codigoSelecionado);
        } else {
          return undefined;
        }
      }
      return props.codigoSelecionado;
    }

    const computedCodigoSelecionado = computed({
      get: () => obterCaracteristicaSubItem(),
      set: (val: number) => {
        emit('update:codigoSelecionado', val);
      },
    });

    function obterCaracteristicaSubItems():number[] {
      obterCaracteristicaSubItemPorCodigo(props.codigosSelecionados);
      return props.codigosSelecionados;
    }

    const computedCodigosSelecionados = computed({
      get: () => obterCaracteristicaSubItems(),
      set: (val: number[]) => {
        emit('update:codigosSelecionados', val);
      },
    });

    function change() {
      emit('change', props.varios ? props.codigosSelecionados : props.codigoSelecionado);
    }

    return {
      props,
      state,
      pesquisarCaracteristicaSubItem,
      computedCodigoSelecionado,
      computedCodigosSelecionados,
      change,
    };
  },
});
